import { clampMapDimension } from "./mapPage";
/** Worldbuilding map file format (`.zwmap` JSON in `Database/`). */

export type {
  MapDocument,
  MapFill,
  MapGrid,
  MapImage,
  MapLabel,
  MapLandmark,
  MapLandmass,
  MapLandmassWorld,
  MapLayer,
  MapNode,
  MapPath,
  MapPathWeight,
  MapPathStyle,
  MapSceneLink,
  MapStoryMarker,
  MapViewport,
} from "./generated/zwMap";

import type {
  MapDocument,
  MapImage,
  MapLabel,
  MapLandmark,
  MapLandmass,
  MapLayer,
  MapNode,
  MapPath,
  MapPathWeight,
  MapFill,
  MapSceneLink,
  MapStoryMarker,
} from "./generated/zwMap";
import {
  atIndexMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
} from "./maybe";
import { trimmedOrNull } from "./trimmedString";

function keepJust<T>(items: readonly Maybe<T>[]): T[] {
  const out: T[] = [];
  for (const item of items) {
    if (isJust(item)) out.push(item.value);
  }
  return out;
}

export const MAP_FILE_EXT = ".zwmap";

export const MAP_ZOOM_MIN = 0.01;
export const MAP_ZOOM_MAX = 16;

export const LANDMARK_BASE_PX = 22;
/** Smallest on-map mark edge, in world pixels (handles + Size slider). */
export const MAP_OBJECT_MIN_PX = 1;
/**
 * Default Size slider floor when native dimensions are unknown.
 * Prefer {@link mapObjectSizeMin} once width/height are known so marks can
 * shrink to {@link MAP_OBJECT_MIN_PX}.
 */
export const LANDMARK_SIZE_MIN = 0.1;
export const LANDMARK_SIZE_MAX = 4;

/** Minimum Size multiplier so the longest native side draws at MAP_OBJECT_MIN_PX. */
export function mapObjectSizeMin(nativeLongestSide: number): number {
  const side = Math.max(MAP_OBJECT_MIN_PX, nativeLongestSide);
  return Math.min(LANDMARK_SIZE_MAX, MAP_OBJECT_MIN_PX / side);
}

export function clampLandmarkSize(value: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(n) === false || n <= 0) return 1;
  // Persist any positive size up to the max; editing clamps to 1px via
  // mapObjectSizeMin / transform handles once native size is known.
  return Math.min(LANDMARK_SIZE_MAX, Math.max(mapObjectSizeMin(1_000_000), n));
}

export function clampMapObjectSize(
  value: number,
  nativeLongestSide: number,
): number {
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(n) === false || n <= 0) return 1;
  return Math.min(
    LANDMARK_SIZE_MAX,
    Math.max(mapObjectSizeMin(nativeLongestSide), n),
  );
}

export function landmarkScale(landmark: Pick<MapLandmark, "size">): number {
  const size = landmark.size;
  if (typeof size === "number" && Number.isFinite(size) && size > 0)
    return Math.min(LANDMARK_SIZE_MAX, size);
  return 1;
}

export function landmarkInk(landmark: Pick<MapLandmark, "color">): string {
  return trimmedOrNull(landmark.color) ?? MAP_DEFAULT_INK;
}

export function storedMapInk(color: string): Maybe<string> {
  const next = trimmedOrNull(color);
  if (next === null) return nothing;
  if (next.toLowerCase() === MAP_DEFAULT_INK.toLowerCase()) return nothing;
  return just(next);
}

export const LABEL_BASE_PX = 14;
export type MapLabelFont =
  | "cartographic"
  | "kauffer-italic"
  | "kauffer-title"
  | "myer-scratch"
  | "serif"
  | "sans";
export const MAP_LABEL_FONTS: Record<MapLabelFont, string> = {
  cartographic: '"Kauffer Hand", Georgia, serif',
  "kauffer-italic": '"Kauffer Italic", Georgia, serif',
  "kauffer-title": '"Kauffer Title", Georgia, serif',
  "myer-scratch": '"Myer Scratch", Georgia, serif',
  serif: 'Georgia, "Times New Roman", serif',
  sans: "Arial, Helvetica, sans-serif",
};

export function clampLabelSize(size: number): number {
  return Number.isFinite(size)
    ? Math.min(200 / LABEL_BASE_PX, Math.max(2 / LABEL_BASE_PX, size))
    : 1;
}

export function labelScale(label: Pick<MapLabel, "size">): number {
  return clampLabelSize(label.size ?? 1);
}

export function labelInk(label: Pick<MapLabel, "color">): string {
  return trimmedOrNull(label.color) ?? MAP_DEFAULT_INK;
}

export function labelFontPx(
  label: Pick<MapLabel, "size">,
  zoom: number,
): number {
  return LABEL_BASE_PX * labelScale(label) * zoom;
}

/** Map-friendly ink / terrain presets (screen editing; greyscale still readable). */
export const MAP_INK_COLORS = [
  { id: "charcoal", label: "Charcoal", value: "#3d3630" },
  { id: "parchment", label: "Parchment", value: "#e1cfaa" },
  { id: "sand", label: "Sand", value: "#cfb989" },
  { id: "stone", label: "Stone", value: "#a39b8a" },
  { id: "rust", label: "Rust", value: "#ad8b70" },
  { id: "forest", label: "Forest", value: "#8b9275" },
  { id: "moss", label: "Moss", value: "#b0b18b" },
  { id: "sea", label: "Sea", value: "#b9c1b4" },
  { id: "gold", label: "Gold", value: "#c1aa75" },
  { id: "off-white", label: "Off-white", value: "#faf8f0" },
] as const;

export const MAP_DEFAULT_INK = MAP_INK_COLORS[0].value;
export const MAP_DEFAULT_FILL = MAP_INK_COLORS[2].value;

/** Warm, light parchment; keeps ink legible in monochrome prints. */
export const MAP_DEFAULT_PAPER = "#eee3c9";

export function mapPaperColor(doc: Pick<MapDocument, "paperColor">): string {
  return trimmedOrNull(doc.paperColor) ?? MAP_DEFAULT_PAPER;
}

/** Missing `paperVisible` means the ocean / paper fill is shown. */
export function isPaperVisible(
  doc: Pick<MapDocument, "paperVisible">,
): boolean {
  return doc.paperVisible !== false;
}

/** Missing `paperLocked` means paint-bucket may recolor the ocean. */
export function isPaperLocked(doc: Pick<MapDocument, "paperLocked">): boolean {
  return doc.paperLocked === true;
}

/** Missing `labelsVisible` means labels are drawn. */
export function isLabelsVisible(
  doc: Pick<MapDocument, "labelsVisible">,
): boolean {
  return doc.labelsVisible !== false;
}

/** Missing `labelsLocked` means the label tool may edit. */
export function isLabelsLocked(
  doc: Pick<MapDocument, "labelsLocked">,
): boolean {
  return doc.labelsLocked === true;
}

/** Persist only when different from the default parchment. */
export function storedMapPaper(color: string): Maybe<string> {
  const next = trimmedOrNull(color);
  if (next === null) return nothing;
  if (next.toLowerCase() === MAP_DEFAULT_PAPER.toLowerCase()) return nothing;
  return just(next);
}

export const PATH_WEIGHT_PX: Record<MapPathWeight, number> = {
  thin: 1.5,
  medium: 2.5,
  thick: 4,
};

export function isMapFileName(name: string): boolean {
  return name.toLowerCase().endsWith(MAP_FILE_EXT);
}

export function newMapId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Current on-disk format. Older maps open and rewrite as this version. */
export const MAP_DOCUMENT_VERSION = 7 as const;

/** Longest side target (world px) when placing a native-size Assets image. */
export const MAP_IMAGE_PLACE_MAX_SIDE = 400;

/** Default Story path ink (dark rust; dash + numbered markers). */
export const MAP_STORY_PATH_COLOR = "#7a4a2e";

export function createDefaultMapDocument(): MapDocument {
  const layerId = newMapId();
  return {
    version: MAP_DOCUMENT_VERSION,
    viewport: { x: 0, y: 0, zoom: 1 },
    grid: { size: 32, visible: true, snap: true },
    activeLayerId: layerId,
    labels: [],
    storyMarkers: [],
    layers: [
      {
        id: layerId,
        name: "Map",
        visible: true,
        locked: false,
        nodes: [],
        paths: [],
        fills: [],
        landmarks: [],
        images: [],
      },
    ],
  };
}

export function mapImageScale(image: Pick<MapImage, "size">): number {
  const size = image.size;
  if (typeof size === "number" && Number.isFinite(size) && size > 0)
    return Math.min(LANDMARK_SIZE_MAX, size);
  return 1;
}

/** Initial `size` so the longest native side fits near MAP_IMAGE_PLACE_MAX_SIDE. */
export function mapImagePlaceSize(width: number, height: number): number {
  const longest = Math.max(width, height);
  if (longest <= 0 || Number.isFinite(longest) === false) return 1;
  if (longest <= MAP_IMAGE_PLACE_MAX_SIDE) return 1;
  return MAP_IMAGE_PLACE_MAX_SIDE / longest;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function normalizeNode(raw: unknown): Maybe<MapNode> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  if (id === null || id.length === 0) return nothing;
  return just({
    id,
    x: asNumber(obj.x, 0),
    y: asNumber(obj.y, 0),
  });
}

function normalizePath(raw: unknown): Maybe<MapPath> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  if (id === null || id.length === 0) return nothing;
  const nodeIds = Array.isArray(obj.nodeIds)
    ? obj.nodeIds
        .map((v) => asString(v).trim())
        .filter((value) => value.length > 0)
    : [];
  const weightRaw = asString(obj.weight);
  const weight: MapPathWeight =
    weightRaw === "thin" || weightRaw === "thick" ? weightRaw : "medium";
  return just({
    id,
    nodeIds,
    color: asString(obj.color, MAP_INK_COLORS[0].value),
    weight,
    ...(obj.style === "dashed" || obj.style === "dotted"
      ? { style: obj.style }
      : {}),
  });
}

function normalizeSceneLink(raw: unknown): Maybe<MapSceneLink> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const file = asString(obj.file).trim();
  if (file.length === 0) return nothing;
  // Drop legacy `label` ("when") if present; chapter/scene already carry that.
  return just({ file });
}

function normalizeSceneLinks(raw: unknown): MapSceneLink[] {
  if (Array.isArray(raw) === false) return [];
  const links: MapSceneLink[] = [];
  for (const entry of raw) {
    const linkM = normalizeSceneLink(entry);
    if (isJust(linkM)) links.push(linkM.value);
  }
  return links;
}

function normalizeMapImage(raw: unknown): Maybe<MapImage> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  const path = asString(obj.path).trim().replace(/\\/g, "/");
  if (id.length === 0 || path.length === 0) return nothing;
  if (path.startsWith("Assets/") === false || path.includes(".."))
    return nothing;
  const width = asNumber(obj.width, 0);
  const height = asNumber(obj.height, 0);
  if (width <= 0 || height <= 0) return nothing;
  const longest = Math.max(width, height);
  const size = clampMapObjectSize(asNumber(obj.size, 1), longest);
  const rotation = ((asNumber(obj.rotation, 0) % 360) + 360) % 360;
  const scaleFloor = mapObjectSizeMin(longest);
  const scaleX = Math.min(40, Math.max(scaleFloor, asNumber(obj.scaleX, 1)));
  const scaleY = Math.min(40, Math.max(scaleFloor, asNumber(obj.scaleY, 1)));
  return just({
    id,
    x: asNumber(obj.x, 0),
    y: asNumber(obj.y, 0),
    path,
    width,
    height,
    ...(rotation !== 0 ? { rotation } : {}),
    ...(scaleX !== 1 ? { scaleX } : {}),
    ...(scaleY !== 1 ? { scaleY } : {}),
    ...(obj.flipX === true ? { flipX: true } : {}),
    ...(obj.flipY === true ? { flipY: true } : {}),
    ...(size !== 1 ? { size } : {}),
  });
}

function normalizeLandmark(raw: unknown): Maybe<MapLandmark> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  if (id === null || id.length === 0) return nothing;
  const iconId = asString(obj.iconId).trim();
  const emoji = asString(obj.emoji).trim();
  if (iconId.length === 0 && emoji.length === 0) return nothing;
  const caption = asString(obj.caption).trim();
  const notes = asString(obj.notes).trim();
  const entityFile = asString(obj.entityFile).trim();
  const sceneLinks = normalizeSceneLinks(obj.sceneLinks);
  const size = clampLandmarkSize(asNumber(obj.size, 1));
  const rotation = ((asNumber(obj.rotation, 0) % 360) + 360) % 360;
  const scaleFloor = mapObjectSizeMin(1_000_000);
  const scaleX = Math.min(40, Math.max(scaleFloor, asNumber(obj.scaleX, 1)));
  const scaleY = Math.min(40, Math.max(scaleFloor, asNumber(obj.scaleY, 1)));
  const color = storedMapInk(asString(obj.color).trim());
  return just({
    id,
    x: asNumber(obj.x, 0),
    y: asNumber(obj.y, 0),
    ...(rotation !== 0 ? { rotation } : {}),
    ...(scaleX !== 1 ? { scaleX } : {}),
    ...(scaleY !== 1 ? { scaleY } : {}),
    ...(obj.flipX === true ? { flipX: true } : {}),
    ...(obj.flipY === true ? { flipY: true } : {}),
    ...(iconId.length > 0 ? { iconId } : {}),
    ...(emoji.length > 0 ? { emoji } : {}),
    ...(size !== 1 ? { size } : {}),
    ...(isJust(color) ? { color: color.value } : {}),
    ...(caption.length > 0 ? { caption } : {}),
    ...(notes.length > 0 ? { notes } : {}),
    ...(entityFile.length > 0 ? { entityFile } : {}),
    ...(sceneLinks.length > 0 ? { sceneLinks } : {}),
  });
}

function normalizeStoryMarker(raw: unknown): Maybe<MapStoryMarker> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  if (id.length === 0) return nothing;
  const sceneLinks = normalizeSceneLinks(obj.sceneLinks);
  const entityFile = asString(obj.entityFile).trim();
  return just({
    id,
    ...(entityFile.length > 0 ? { entityFile } : {}),
    x: asNumber(obj.x, 0),
    y: asNumber(obj.y, 0),
    ...(sceneLinks.length > 0 ? { sceneLinks } : {}),
  });
}

function normalizeLabel(raw: unknown): Maybe<MapLabel> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  const text = asString(obj.text).trim();
  if (id === null || id.length === 0 || text === null || text.length === 0)
    return nothing;
  const size = clampLabelSize(asNumber(obj.size, 1));
  const curve = Math.max(-180, Math.min(180, asNumber(obj.curve, 0)));
  const letterSpacing = Math.max(
    0,
    Math.min(24, asNumber(obj.letterSpacing, 0)),
  );
  const color = storedMapInk(asString(obj.color).trim());
  return just({
    id,
    x: asNumber(obj.x, 0),
    y: asNumber(obj.y, 0),
    text,
    ...(curve !== 0 ? { curve } : {}),
    ...(letterSpacing !== 0 ? { letterSpacing } : {}),
    ...(obj.font === "kauffer-italic" ||
    obj.font === "kauffer-title" ||
    obj.font === "myer-scratch" ||
    obj.font === "serif" ||
    obj.font === "sans"
      ? { font: obj.font }
      : {}),
    ...(asNumber(obj.rotation, 0) !== 0
      ? { rotation: asNumber(obj.rotation, 0) }
      : {}),
    ...(size !== 1 ? { size } : {}),
    ...(isJust(color) ? { color: color.value } : {}),
  });
}

function normalizeFill(raw: unknown): Maybe<MapFill> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  if (id === null || id.length === 0) return nothing;
  const nodeIds = Array.isArray(obj.nodeIds)
    ? obj.nodeIds
        .map((v) => asString(v).trim())
        .filter((value) => value.length > 0)
    : [];
  if (nodeIds.length < 3) return nothing;
  return just({
    id,
    nodeIds,
    color: asString(obj.color, MAP_DEFAULT_FILL),
  });
}

function normalizeLandmass(raw: unknown): Maybe<MapLandmass> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const path = asString(obj.path).trim();
  if (path.length === 0) return nothing;
  const widthRaw = asNumber(obj.width, NaN);
  const heightRaw = asNumber(obj.height, NaN);
  if (
    Number.isFinite(widthRaw) === false ||
    Number.isFinite(heightRaw) === false
  ) {
    return nothing;
  }
  const width = Math.floor(widthRaw);
  const height = Math.floor(heightRaw);
  if (width < 1 || height < 1) return nothing;
  const worldRaw =
    obj.world !== null && typeof obj.world === "object"
      ? (obj.world as Record<string, unknown>)
      : null;
  if (worldRaw === null) return nothing;
  const w = asNumber(worldRaw.w, 0);
  const h = asNumber(worldRaw.h, 0);
  if (w === 0 || h === 0) return nothing;
  return just({
    path,
    width,
    height,
    world: {
      x: asNumber(worldRaw.x, 0),
      y: asNumber(worldRaw.y, 0),
      w,
      h,
    },
    ...(asBool(obj.visible, true) === false ? { visible: false } : {}),
    ...(asBool(obj.locked, false) === true ? { locked: true } : {}),
  });
}

function edgeKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function pathEdgeKeys(layer: MapLayer): Set<string> {
  const edges = new Set<string>();
  for (const path of layer.paths) {
    for (let i = 0; i < path.nodeIds.length - 1; i++) {
      const a = path.nodeIds[i]!;
      const b = path.nodeIds[i + 1]!;
      if (a !== null && a.length > 0 && b !== null && b.length > 0 && a !== b)
        edges.add(edgeKey(a, b));
    }
  }
  return edges;
}

function pruneInvalidFills(layer: MapLayer): void {
  const nodeIds = new Set(layer.nodes.map((n) => n.id));
  const edges = pathEdgeKeys(layer);
  layer.fills = layer.fills.filter((fill) => {
    if (fill.nodeIds.length < 3) return false;
    for (let i = 0; i < fill.nodeIds.length; i++) {
      const a = fill.nodeIds[i]!;
      const b = fill.nodeIds[(i + 1) % fill.nodeIds.length]!;
      if (nodeIds.has(a) === false || edges.has(edgeKey(a, b)) === false)
        return false;
    }
    return true;
  });
}

/** Drop fills whose border is gone, then anchors no path or fill references. */
export function pruneOrphanNodes(layer: MapLayer): void {
  pruneInvalidFills(layer);
  const used = new Set<string>();
  for (const path of layer.paths) {
    for (const id of path.nodeIds) used.add(id);
  }
  for (const fill of layer.fills) {
    for (const id of fill.nodeIds) used.add(id);
  }
  layer.nodes = layer.nodes.filter((n) => used.has(n.id));
}

type NormalizedLayer = {
  layer: MapLayer;
  /** Legacy per-layer labels lifted to the document Labels layer (v5). */
  legacyLabels: MapLabel[];
};

function normalizeLayer(raw: unknown): Maybe<NormalizedLayer> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  if (id === null || id.length === 0) return nothing;
  const legacyLabels = Array.isArray(obj.labels)
    ? keepJust(obj.labels.map(normalizeLabel))
    : [];
  const layer: MapLayer = {
    id,
    name: trimmedOrNull(asString(obj.name, "Layer")) ?? "Layer",
    opacity:
      typeof obj.opacity === "number" && Number.isFinite(obj.opacity)
        ? Math.max(0, Math.min(1, obj.opacity))
        : 1,
    visible: asBool(obj.visible, true),
    locked: asBool(obj.locked, false),
    nodes: Array.isArray(obj.nodes)
      ? keepJust(obj.nodes.map(normalizeNode))
      : [],
    paths: Array.isArray(obj.paths)
      ? keepJust(obj.paths.map(normalizePath))
      : [],
    fills: Array.isArray(obj.fills)
      ? keepJust(obj.fills.map(normalizeFill))
      : [],
    landmarks: Array.isArray(obj.landmarks)
      ? keepJust(obj.landmarks.map(normalizeLandmark))
      : [],
    images: Array.isArray(obj.images)
      ? keepJust(obj.images.map(normalizeMapImage))
      : [],
  };
  pruneOrphanNodes(layer);
  return just({ layer, legacyLabels });
}

export type ParsedMapDocument = {
  doc: MapDocument;
  /** True when the raw string was unusable and a default doc should be written back. */
  healed: boolean;
};

/**
 * Parse `.zwmap` JSON. Corrupt / empty-layer / non-object input yields a
 * default document with `healed: true` so callers can rewrite disk (heal-on-read).
 */
export function parseMapDocumentWithHeal(raw: string): ParsedMapDocument {
  const fallback = createDefaultMapDocument();
  if (raw.trim().length === 0) {
    return { doc: fallback, healed: false };
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== "object") {
      return { doc: fallback, healed: true };
    }
    const obj = parsed as Record<string, unknown>;
    const normalizedLayers = Array.isArray(obj.layers)
      ? keepJust(obj.layers.map(normalizeLayer))
      : [];
    if (normalizedLayers.length === 0) {
      return { doc: fallback, healed: true };
    }
    const layers = normalizedLayers.map((entry) => entry.layer);
    const legacyLayerLabels = normalizedLayers.flatMap(
      (entry) => entry.legacyLabels,
    );
    const docLabels = Array.isArray(obj.labels)
      ? keepJust(obj.labels.map(normalizeLabel))
      : [];
    // Prefer document labels; append any leftover per-layer labels (pre-v5).
    const seenLabelIds = new Set(docLabels.map((label) => label.id));
    const labels = [
      ...docLabels,
      ...legacyLayerLabels.filter((label) => {
        if (seenLabelIds.has(label.id)) return false;
        seenLabelIds.add(label.id);
        return true;
      }),
    ];
    // Prefer storyMarkers; ignore leftover unfinished `journeys` WIP if present.
    const storyMarkers = Array.isArray(obj.storyMarkers)
      ? keepJust(obj.storyMarkers.map(normalizeStoryMarker))
      : [];
    const viewportRaw =
      obj.viewport !== null && typeof obj.viewport === "object"
        ? (obj.viewport as Record<string, unknown>)
        : {};
    const gridRaw =
      obj.grid !== null && typeof obj.grid === "object"
        ? (obj.grid as Record<string, unknown>)
        : {};
    const activeLayerId = asString(obj.activeLayerId).trim();
    const activeExists = layers.some((l) => l.id === activeLayerId);
    // Legacy document `background` is dropped on heal (#279); no MapImage migration.
    const landmassM = normalizeLandmass(obj.landmass);
    const paperM = storedMapPaper(asString(obj.paperColor).trim());
    const firstLayerM = atIndexMaybe(layers, 0);
    if (isNothing(firstLayerM)) {
      return { doc: fallback, healed: true };
    }
    const firstLayer = firstLayerM.value;
    return {
      doc: {
        version: MAP_DOCUMENT_VERSION,
        viewport: {
          x: asNumber(viewportRaw.x, 0),
          y: asNumber(viewportRaw.y, 0),
          zoom: Math.max(
            MAP_ZOOM_MIN,
            Math.min(MAP_ZOOM_MAX, asNumber(viewportRaw.zoom, 1)),
          ),
        },
        grid: {
          size: Math.max(8, asNumber(gridRaw.size, 32)),
          visible: asBool(gridRaw.visible, true),
          snap: asBool(gridRaw.snap, true),
        },
        ...(obj.page !== null && typeof obj.page === "object"
          ? {
              page: {
                ...(asBool(
                  (obj.page as Record<string, unknown>).fitOnOpen,
                  false,
                )
                  ? { fitOnOpen: true }
                  : {}),
                ...(asNumber(
                  (obj.page as Record<string, unknown>).centerX,
                  0,
                ) !== 0
                  ? {
                      centerX: asNumber(
                        (obj.page as Record<string, unknown>).centerX,
                        0,
                      ),
                    }
                  : {}),
                ...(asNumber(
                  (obj.page as Record<string, unknown>).centerY,
                  0,
                ) !== 0
                  ? {
                      centerY: asNumber(
                        (obj.page as Record<string, unknown>).centerY,
                        0,
                      ),
                    }
                  : {}),
                width: clampMapDimension(
                  asNumber((obj.page as Record<string, unknown>).width, 2400),
                ),
                height: clampMapDimension(
                  asNumber((obj.page as Record<string, unknown>).height, 1600),
                ),
                frame:
                  (["pencil", "double", "ornate"] as const).find(
                    (frame) =>
                      frame === (obj.page as Record<string, unknown>).frame,
                  ) ?? "none",
              },
            }
          : {}),
        ...(isJust(landmassM) ? { landmass: landmassM.value } : {}),
        ...(isJust(paperM) ? { paperColor: paperM.value } : {}),
        ...(asBool(obj.paperVisible, true) === false
          ? { paperVisible: false }
          : {}),
        ...(asBool(obj.paperLocked, false) === true
          ? { paperLocked: true }
          : {}),
        ...(asBool(obj.labelsVisible, true) === false
          ? { labelsVisible: false }
          : {}),
        ...(asBool(obj.labelsLocked, false) === true
          ? { labelsLocked: true }
          : {}),
        labels,
        storyMarkers,
        activeLayerId: activeExists ? activeLayerId : firstLayer.id,
        layers,
      },
      healed: false,
    };
  } catch {
    return { doc: fallback, healed: true };
  }
}

export function parseMapDocument(raw: string): MapDocument {
  return parseMapDocumentWithHeal(raw).doc;
}

export function serializeMapDocument(doc: MapDocument): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

export function activeMapLayer(doc: MapDocument): MapLayer {
  return doc.layers.find((l) => l.id === doc.activeLayerId) ?? doc.layers[0]!;
}

export function cloneMapDocument(doc: MapDocument): MapDocument {
  return parseMapDocument(serializeMapDocument(doc));
}
